import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import toast from 'react-hot-toast';
import { Heart, Pencil, Trash2 } from 'lucide-react';
import { Profile } from '../../types';
import { BACKEND_URL } from '../../config';
import { authHeaders } from '../../api';
import { FormSection } from '../ui';
import FormShell from './FormShell';

const emptyProfile: Profile = {
  name: '', position: {}, description: {}, linkedIn: '', github: '', mail: '', phone: '', localisation: '', interests: {}, openToWork: true,
};

interface ProfileFormProps {
  profile: Profile | null;
  onSaved: (profile: Profile) => void;
}

export default function ProfileForm({ profile, onSaved }: ProfileFormProps) {
  const { t, i18n } = useTranslation();
  const [lang, setLang] = useState(i18n.language.startsWith('fr') ? 'fr' : 'en');
  const initial = profile || emptyProfile;

  const [editProfile, setEditProfile] = useState<Profile>({ ...initial });
  const [saving, setSaving] = useState(false);
  const [picture, setPicture] = useState<File | null>(null);
  const [picturePreview, setPicturePreview] = useState('');
  const [interestInput, setInterestInput] = useState('');

  const reset = () => {
    setEditProfile({ ...initial });
    setPicture(null);
    setPicturePreview('');
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/profile`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(editProfile),
      });
      if (!res.ok) throw new Error();
      if (picture) {
        const fd = new FormData();
        fd.append('file', picture);
        const up = await fetch(`${BACKEND_URL}/api/upload-pp`, { method: 'POST', headers: authHeaders(), body: fd });
        if (!up.ok) throw new Error();
      }
      onSaved(editProfile);
      setPicture(null);
      toast.success(t('saved'));
    } catch {
      toast.error(t('error_saving'));
    } finally {
      setSaving(false);
    }
  };

  const interests = editProfile.interests[lang] || [];
  const setInterests = (list: { name: string }[]) =>
    setEditProfile({ ...editProfile, interests: { ...editProfile.interests, [lang]: list } });

  const addInterest = () => {
    const name = interestInput.trim();
    if (!name) return;
    setInterests([...interests, { name }].sort((a, b) => a.name.localeCompare(b.name)));
    setInterestInput('');
  };

  const pickPicture = (f?: File) => {
    if (f) { setPicture(f); setPicturePreview(URL.createObjectURL(f)); }
  };

  const inputCls = "w-full glass rounded-lg px-3.5 py-2.5 text-zinc-200 placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-primary/40 transition text-sm";
  const labelCls = "block text-xs font-medium text-zinc-400 mb-1.5";
  const set = (k: keyof Profile) => (e: React.ChangeEvent<HTMLInputElement>) => setEditProfile({ ...editProfile, [k]: e.target.value });
  const setLoc = (k: 'position' | 'tagline') => (e: React.ChangeEvent<HTMLInputElement>) =>
    setEditProfile({ ...editProfile, [k]: { ...editProfile[k], [lang]: e.target.value } });

  const firstName = editProfile.name.split(' ')[0];
  const tagline = editProfile.tagline?.[lang]?.trim() ? editProfile.tagline[lang].replace('{name}', firstName) : t('hello', { name: firstName, lng: lang });
  const open = editProfile.openToWork !== false;

  const sections: [string, string][] = [
    ['pr-tagline', String(t('hero_title'))],
    ['pr-identity', String(t('identity'))],
    ['pr-contact', 'Contact'],
    ['pr-bio', 'Bio'],
    ['pr-interests', String(t('interests'))],
  ];

  return (
    <FormShell
      onSubmit={handleSave}
      onCancel={reset}
      submitting={saving}
      submitLabel={String(t('save'))}
      sections={sections}
      side={
        <div className="rounded-2xl border border-line p-5">
          <p className="font-display font-extrabold tracking-[-0.02em] text-lg text-ink">{t('language')}</p>
          <div role="group" aria-label={t('language') as string} className="mt-2 inline-flex rounded-full border border-line p-1">
            {['fr', 'en'].map(l => (
              <button key={l} type="button" onClick={() => setLang(l)} aria-pressed={lang === l}
                className={`h-9 px-4 rounded-full text-sm font-semibold uppercase ${lang === l ? 'bg-ink text-base' : 'text-zinc-500 hover:text-ink'}`}>{l}</button>
            ))}
          </div>
          <p className="text-xs text-zinc-500 mt-2">{t('profile_edit_lang')}</p>
        </div>
      }
    >
      <FormSection id="pr-tagline" title={String(t('hero_title'))}>
        <label className="block">
          <span className={labelCls}>{t('tagline')}</span>
          <input className={inputCls} placeholder={t('hello', { name: '{name}', lng: lang }) as string} value={editProfile.tagline?.[lang] || ''} onChange={setLoc('tagline')} />
          <span className="block text-xs text-zinc-500 mt-1.5">{t('tagline_hint')}</span>
        </label>
        <p className="font-display text-3xl sm:text-4xl font-extrabold tracking-[-0.03em] text-ink pt-2">{tagline}</p>
      </FormSection>

      <FormSection id="pr-identity" title={String(t('identity'))}>
        <div className="flex flex-col sm:flex-row items-center gap-5">
          <label
            className="relative cursor-pointer group flex-shrink-0"
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); pickPicture(Array.from(e.dataTransfer.files).find(f => f.type.startsWith('image/'))); }}
          >
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden bg-zinc-800 ring-2 ring-ink/10 group-hover:ring-primary/40 transition-all">
              <img src={picturePreview || profile?.picture_url || `${BACKEND_URL}/profile-picture.png`} alt="Profile" className="w-full h-full object-cover" />
            </div>
            <div className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <Pencil size={16} className="text-white" />
            </div>
            <input type="file" accept="image/*" className="hidden" onChange={e => pickPicture(e.target.files?.[0])} />
          </label>
          <div className="flex-1 w-full space-y-3">
            <label className="block">
              <span className={labelCls}>{t('name')}</span>
              <input className={inputCls} value={editProfile.name} onChange={set('name')} />
            </label>
            <label className="block">
              <span className={labelCls}>{t('position_placeholder')}</span>
              <input className={inputCls} value={editProfile.position[lang] || ''} onChange={setLoc('position')} />
            </label>
          </div>
        </div>
        <div className="flex items-center justify-between gap-4 pt-4 border-t border-line">
          <div>
            <p className="text-sm font-medium text-ink">{t('open_to_work')}</p>
            <p className="text-xs text-zinc-500 mt-0.5">{t('open_to_work_desc')}</p>
          </div>
          <button type="button" role="switch" aria-checked={open} aria-label={t('open_to_work') as string}
            onClick={() => setEditProfile({ ...editProfile, openToWork: !open })}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-200 ${open ? 'bg-primary' : 'bg-zinc-700'}`}>
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${open ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>
      </FormSection>

      <FormSection id="pr-contact" title="Contact">
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block"><span className={labelCls}>{t('email')}</span>
            <input className={inputCls} value={editProfile.mail} onChange={set('mail')} /></label>
          <label className="block"><span className={labelCls}>{t('phone')}</span>
            <input className={inputCls} value={editProfile.phone} onChange={set('phone')} /></label>
          <label className="block"><span className={labelCls}>LinkedIn</span>
            <input className={inputCls} placeholder={t('linkedin_url_profile') as string} value={editProfile.linkedIn} onChange={set('linkedIn')} /></label>
          <label className="block"><span className={labelCls}>GitHub</span>
            <input className={inputCls} placeholder={t('github_url_profile') as string} value={editProfile.github} onChange={set('github')} /></label>
        </div>
        <label className="block"><span className={labelCls}>{t('location_placeholder')}</span>
          <input className={inputCls} value={editProfile.localisation} onChange={set('localisation')} /></label>
      </FormSection>

      <FormSection id="pr-bio" title={`Bio (${lang.toUpperCase()})`}>
        <ReactQuill value={editProfile.description?.[lang] || ''}
          onChange={v => setEditProfile(p => ({ ...p, description: { ...p.description, [lang]: v } }))} />
      </FormSection>

      <FormSection id="pr-interests" title={`${t('interests')} (${lang.toUpperCase()})`} icon={<Heart size={18} />}>
        {interests.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {interests.map(interest => (
              <span key={interest.name} className="flex items-center gap-1.5 text-xs glass px-3 py-1.5 rounded-full text-zinc-300">
                {interest.name}
                <button type="button" onClick={() => setInterests(interests.filter(i => i.name !== interest.name))}
                  className="text-red-500 hover:text-red-600 transition-colors" aria-label={`${t('delete')} ${interest.name}`}><Trash2 size={12} /></button>
              </span>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2">
          <input className={inputCls} value={interestInput} onChange={e => setInterestInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addInterest(); } }}
            placeholder={t('add_interest') as string} aria-label={t('add_interest') as string} />
          <button type="button" onClick={addInterest}
            className="h-11 px-5 rounded-full border border-line text-sm font-medium text-ink hover:bg-surface transition-colors flex-shrink-0">+</button>
        </div>
      </FormSection>
    </FormShell>
  );
}
